import { useApi } from "@/hooks/useApi";
import type { CrmIntegrationStatusDTO } from "@shaarp/common";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type HubSpotIntegrationContextType = {
  /**
   * Served by `GET /integration/crm/hubspot`. `isAvailable` is false when the
   * connection still exists but HubSpot rejected its grant, so it serves
   * nothing until the user reconnects.
   */
  hubspotIntegration: CrmIntegrationStatusDTO | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

const HubSpotIntegrationContext = createContext<HubSpotIntegrationContextType>({
  hubspotIntegration: null,
  isLoading: true,
  refetch: async () => {},
});

export const useHubSpotIntegration = (): HubSpotIntegrationContextType => {
  const context = useContext(HubSpotIntegrationContext);
  if (!context) {
    throw new Error(
      "useHubSpotIntegration must be used within a HubSpotIntegrationProvider"
    );
  }
  return context;
};

export const HubSpotIntegrationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const api = useApi();
  const [hubspotIntegration, setHubspotIntegration] =
    useState<CrmIntegrationStatusDTO | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchHubSpotIntegration = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<CrmIntegrationStatusDTO>(
        "/integration/crm/hubspot"
      );
      setHubspotIntegration(data);
    } catch (error) {
      setHubspotIntegration(null);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const refetch = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    await fetchHubSpotIntegration();
  }, [fetchHubSpotIntegration]);

  useEffect(() => {
    fetchHubSpotIntegration();
  }, [fetchHubSpotIntegration]);

  const value = useMemo(
    () => ({
      hubspotIntegration,
      isLoading,
      refetch,
    }),
    [hubspotIntegration, isLoading, refetch],
  );

  return (
    <HubSpotIntegrationContext.Provider value={value}>
      {children}
    </HubSpotIntegrationContext.Provider>
  );
};
